// src/components/student/StudentExperience.js
//
// Pass 82D §24/§32/§37 — the REAL student presentation, consuming EXPLICIT identity so both the genuine student
// workspace and the authorized staff Student Preview render the SAME surfaces from the SAME canonical data — never a
// forked mock. Identity is passed in (studentAuthUid + organizationId), never derived from the signed-in user here.
// `viewerMode` sets the ACTION CONTRACT: 'student' keeps the student's real actions; 'staff_preview' is strictly
// READ-ONLY (persistent banner, no write actions) and every read is ORG-SCOPED + zero-write (studentDataAccess).

import React, { useEffect, useMemo, useState } from 'react';
import { db } from '../../firebase';
import { loadStudentVideos, loadStudentLessonsReadOnly, loadStudentReviewsReadOnly } from '../../utils/studentDataAccess';
import { useStudentModernVideos, useStudentLessonProjections } from '../../hooks/useStudentModernData';

const C = { bg: '#FAFAF8', card: '#FFFFFF', border: 'rgba(0,0,0,0.08)', blue: '#2563EB', t1: '#1C1B18', t2: '#57564F', t3: '#8A8980', amber: '#B45309', amberBg: '#FEF3C7' };
const font = "'DM Sans',system-ui,sans-serif";

const TABS = [['home', 'Home'], ['videos', 'My Videos'], ['lessons', 'Lessons'], ['progress', 'Progress']];

// Pass 85.1A — `participantId` (optional) selects the MODERN canonical path: org+participant-scoped videos + safe
// lesson PROJECTIONS. When absent, the LEGACY Auth-uid path is used (back-compat for surfaces not yet on the canonical
// participant model). Either way identity is INJECTED and every read is org-scoped + read-only.
export default function StudentExperience({ studentAuthUid = '', organizationId = '', participantId = '', studentName = '', viewerMode = 'student' }) {
  const readOnly = viewerMode === 'staff_preview';
  const modern = !!participantId;
  const [tab, setTab] = useState('home');

  // Pass 85.1A.1 (§7/§8/§9) — the MODERN path uses the SCOPE-BOUND hooks so a participant/org switch (A→B) suppresses
  // stale data SYNCHRONOUSLY at render, BEFORE the new read resolves — never a stale flash. The scope key includes the
  // org + participantId (a viewer token fills the uid slot for staff preview, where there is no student Auth uid).
  const viewerKey = studentAuthUid || (readOnly ? 'staff_preview' : '');
  const mv = useStudentModernVideos(modern ? viewerKey : '', modern ? organizationId : '', modern ? participantId : '');
  const ml = useStudentLessonProjections(modern ? viewerKey : '', modern ? organizationId : '', modern ? participantId : '');

  // LEGACY path (no participantId) — scope-keyed effect so a studentAuthUid/org switch also suppresses stale at render.
  const legacyScopeKey = !modern && studentAuthUid ? `${studentAuthUid}::${organizationId}` : '';
  const [legacy, setLegacy] = useState({ scopeKey: '', videos: [], lessons: [], reviews: [], loading: false, error: null });
  useEffect(() => {
    let alive = true;
    if (!legacyScopeKey) { setLegacy({ scopeKey: legacyScopeKey, videos: [], lessons: [], reviews: [], loading: false, error: null }); return undefined; }
    setLegacy({ scopeKey: legacyScopeKey, videos: [], lessons: [], reviews: [], loading: true, error: null });
    (async () => {
      try {
        const [v, l, r] = await Promise.all([
          loadStudentVideos(db, { studentAuthUid, organizationId }),
          loadStudentLessonsReadOnly(db, { studentAuthUid, organizationId }),
          loadStudentReviewsReadOnly(db, { studentAuthUid, organizationId }),
        ]);
        if (alive) setLegacy({ scopeKey: legacyScopeKey, videos: v, lessons: l, reviews: r, loading: false, error: null });
      } catch (e) { if (alive) setLegacy({ scopeKey: legacyScopeKey, videos: [], lessons: [], reviews: [], loading: false, error: e || new Error('legacy_read_failed') }); }
    })();
    return () => { alive = false; };
  }, [studentAuthUid, organizationId, legacyScopeKey]);
  const legacyView = useMemo(
    () => (legacy.scopeKey === legacyScopeKey ? legacy : { videos: [], lessons: [], reviews: [], loading: !!legacyScopeKey, error: null }),
    [legacy, legacyScopeKey],
  );

  // Unify the two paths behind one render contract (loading distinct from empty; a read error is surfaced, never empty).
  const { videos, lessons, reviews, loading, err } = useMemo(() => {
    if (modern) return { videos: mv.videos, lessons: ml.lessons, reviews: [], loading: mv.loading || ml.loading, err: (mv.error || ml.error) ? 'Could not load this student’s data.' : '' };
    return { videos: legacyView.videos, lessons: legacyView.lessons, reviews: legacyView.reviews, loading: legacyView.loading, err: legacyView.error ? 'Could not load this student’s data.' : '' };
  }, [modern, mv.videos, mv.loading, mv.error, ml.lessons, ml.loading, ml.error, legacyView]);

  const reviewed = (videos || []).filter((v) => v.status === 'reviewed').length;
  const inReview = (videos || []).filter((v) => v.status === 'in_review' || v.status === 'pending').length;

  return (
    <div data-testid="student-experience" data-viewer-mode={viewerMode} style={{ fontFamily: font, color: C.t1, background: C.bg, minHeight: '100%', padding: 20 }}>
      {readOnly && (
        <div data-testid="preview-readonly-banner" style={{ position: 'sticky', top: 0, zIndex: 5, background: C.amberBg, border: `1px solid #FCD34D`, color: C.amber, borderRadius: 10, padding: '9px 14px', marginBottom: 14, fontSize: 13, fontWeight: 700 }}>
          READ-ONLY STUDENT PREVIEW — {studentName || 'Student'}{organizationId ? ` · ${organizationId}` : ''}. You are inspecting this student’s experience; nothing you do here is saved.
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {TABS.map(([k, l]) => (
          <button key={k} data-testid={`se-tab-${k}`} onClick={() => setTab(k)} style={{ padding: '6px 14px', borderRadius: 9, border: `1px solid ${tab === k ? C.t1 : C.border}`, background: tab === k ? C.t1 : '#fff', color: tab === k ? '#fff' : C.t2, fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>{l}</button>
        ))}
      </div>

      {err && <div data-testid="se-error" style={{ color: '#DC2626', fontSize: 13, marginBottom: 12 }}>{err}</div>}

      {tab === 'home' && (
        <div data-testid="se-home">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, marginBottom: 16 }}>
            <Stat label="Videos" value={(videos || []).length} />
            <Stat label="Reviewed" value={reviewed} />
            <Stat label="Awaiting feedback" value={inReview} />
          </div>
          <Section title="Recent reviews">
            {(reviews || []).length === 0 ? <Empty text="No reviews yet." /> : (reviews || []).slice(0, 5).map((r) => (
              <div key={r.id} style={row}>{r.noteText ? String(r.noteText).slice(0, 120) : 'Review'}</div>
            ))}
          </Section>
        </div>
      )}

      {tab === 'videos' && (
        <Section title="My Videos" testid="se-videos">
          {loading ? <Empty text="Loading…" /> : err ? <Empty text="Could not load videos." /> : videos.length === 0 ? <Empty text="No videos in this organisation." /> : videos.map((v) => (
            <div key={v.id} style={row}>{v.videoName || v.title || 'Video'} · <span style={{ color: C.t3 }}>{v.status || 'pending'}</span></div>
          ))}
          {/* No upload action in staff preview (read-only §25). The genuine student workspace offers upload elsewhere. */}
        </Section>
      )}

      {tab === 'lessons' && (
        <Section title="Lessons" testid="se-lessons">
          {loading ? <Empty text="Loading…" /> : err ? <Empty text="Could not load lessons." /> : lessons.length === 0 ? (
            <Empty text={modern ? 'No lessons have been shared with you yet.' : 'No lessons in this organisation.'} />
          ) : lessons.map((l) => (
            modern ? (
              <div key={l.id} style={{ ...row, whiteSpace: 'normal' }}>
                <div style={{ fontWeight: 600 }}>{l.title || l.lessonDate || 'Lesson'}{l.discipline ? <span style={{ color: C.t3, fontWeight: 400 }}> · {l.discipline}</span> : null}</div>
                {l.summary ? <div style={{ color: C.t2, marginTop: 3 }}>{String(l.summary).slice(0, 240)}</div> : null}
                {Array.isArray(l.focusAreas) && l.focusAreas.length ? <div style={{ color: C.t3, marginTop: 3, fontSize: 12 }}>Focus: {l.focusAreas.join(', ')}</div> : null}
              </div>
            ) : (
              <div key={l.id} style={row}>{l.serviceDate || l.date || 'Lesson'} · <span style={{ color: C.t3 }}>{l.status || ''}</span></div>
            )
          ))}
        </Section>
      )}

      {tab === 'progress' && (
        <Section title="Progress" testid="se-progress">
          <div style={{ fontSize: 13, color: C.t2 }}>
            {reviewed > 0 ? `${reviewed} reviewed video${reviewed === 1 ? '' : 's'} of coaching evidence.` : 'No reviewed coaching evidence yet.'}
          </div>
          <Empty text="Progression is derived from reviewed coaching evidence and delivered lessons." />
        </Section>
      )}
    </div>
  );
}

const cardBox = { background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 14 };
const row = { padding: '8px 10px', borderBottom: `1px solid ${C.border}`, fontSize: 13 };
function Stat({ label, value }) {
  return <div style={{ ...cardBox, textAlign: 'center' }}><div style={{ fontSize: 26, fontWeight: 800, color: C.blue }}>{value}</div><div style={{ fontSize: 12, color: C.t3 }}>{label}</div></div>;
}
function Section({ title, children, testid }) {
  return <div data-testid={testid} style={{ ...cardBox, marginBottom: 14 }}><div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>{title}</div>{children}</div>;
}
function Empty({ text }) { return <div style={{ fontSize: 12.5, color: C.t3, padding: '6px 0' }}>{text}</div>; }
